import { Alert, Col, ConfigProvider, Descriptions, Divider, Row, Spin, Table, Tag, Typography } from "antd";
import frFR from "antd/locale/fr_FR";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SDIModal from "../../../../components/ui/SDIModal";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { frenchDateOnly } from "../../../../variables/functions";

const { Text, Title } = Typography;

const formatMontant = (value) =>
    `${new Intl.NumberFormat("fr-FR").format(Math.round(value || 0))} FCFA`;

// Avenant d'annulation (AA) : annule l'avenant EN COURS, payé et encaissé. Le contrat revient à son
// état d'avant cet avenant ; ristourne = toute la prime de l'avenant (PN + coût de pièces + taxes),
// sans prorata.
// Le backend calcule l'éligibilité, les engins qui sortent et les montants.
const AnnulationEngins = (props) => {
    const [open, setOpen] = useState(props.open);
    const [chargement, setChargement] = useState(false);
    const [envoi, setEnvoi] = useState(false);
    const [apercu, setApercu] = useState(null);
    const [erreur, setErreur] = useState(null);
    const navigate = useNavigate();

    // Message métier du backend s'il existe ; sinon un message simple (le détail est dans la console).
    const messageErreur = (error, defaut) => {
        if (error?.response?.data?.message) return error.response.data.message;
        if (!error?.response?.status) return `${defaut} Serveur injoignable.`;
        return `${defaut} Réessayez ou contactez l'administrateur.`;
    };

    useEffect(() => {
        setOpen(props.open);
        if (!props.open || !props?.contrat?.codeId) return;
        setApercu(null);
        setErreur(null);
        setChargement(true);
        axiosInstance.put("/api/contrat/annulation-engins-preview", { contratCodeId: props.contrat.codeId })
            .then((response) => setApercu(response.data))
            .catch((error) => {
                console.error(error);
                setErreur(messageErreur(error, "Impossible de calculer l'annulation."));
            })
            .finally(() => setChargement(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props?.contrat?.codeId]);

    const fermer = () => {
        setOpen(false);
        props.update(false);
    };

    const valider = async () => {
        setEnvoi(true);
        try {
            const response = await axiosInstance.put("/api/contrat/annulation-engins", {
                contratCodeId: props?.contrat?.codeId,
            });
            toast.success("Avenant annulé. Réglez la ristourne en caisse pour imprimer le reçu.");
            fermer();
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();
        } catch (error) {
            console.error(error);
            toast.error(messageErreur(error, "Erreur lors de l'annulation de l'avenant."));
        } finally {
            setEnvoi(false);
        }
    };

    const colonnes = [
        { title: "Immatriculation", dataIndex: "immatriculation", render: (v) => <Text strong>{v || "—"}</Text> },
        { title: "Marque", dataIndex: "marque", render: (v) => v || "—" },
        { title: "Type", dataIndex: "type", render: (v) => v || "—" },
    ];

    return (
        <SDIModal
            title="AVENANT D'ANNULATION"
            centered
            open={open}
            onOk={valider}
            onCancel={fermer}
            confirmLoading={envoi}
            okButtonProps={{ danger: true, disabled: chargement || !!erreur || !apercu?.eligible }}
            width={900}
            zIndex={1940}
            okText="Annuler l'avenant"
            cancelText="Fermer"
        >
            <ConfigProvider locale={frFR}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Annulation de l'avenant en cours"
                    description={
                        "Le contrat revient à son état d'avant cet avenant. La ristourne est égale à la totalité "
                        + "de la prime payée pour l'avenant : prime nette, coût de pièces et taxes (remises "
                        + "comprises), sans prorata. La ristourne passe en caisse (mode Ristourne), qui imprime "
                        + "ensuite le reçu."
                    }
                />
                {erreur && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={erreur} />}
                <Spin spinning={chargement}>
                    {apercu && (
                        <>
                            {!apercu.eligible && (
                                <Alert
                                    type="error"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                    message={`Annulation impossible : ${apercu.motif}.`}
                                />
                            )}
                            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
                                <Descriptions.Item label="Avenant annulé">
                                    {[apercu.nomAvenant, apercu.numeroAvenant].filter(Boolean).join(" — ") || "—"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Prise d'effet">
                                    {apercu.dateEffet ? frenchDateOnly(apercu.dateEffet) : "—"}
                                    {apercu.effetPasse && (
                                        <Tag color="orange" style={{ marginLeft: 8 }}>Déjà en vigueur</Tag>
                                    )}
                                </Descriptions.Item>
                            </Descriptions>
                            {apercu.engins?.length > 0 && (
                                <>
                                    <Text strong>Engins qui sortent du contrat</Text>
                                    <Table
                                        rowKey="enginCodeId"
                                        size="small"
                                        pagination={false}
                                        columns={colonnes}
                                        dataSource={apercu.engins}
                                        style={{ marginTop: 8 }}
                                    />
                                </>
                            )}
                            <div style={{ marginTop: 16, padding: 12, background: "#f8f9fa", borderRadius: 6 }}>
                                <Title level={5} style={{ marginTop: 0 }}>Récapitulatif de la ristourne</Title>
                                <Row justify="space-between">
                                    <Col><Text>Prime nette</Text></Col>
                                    <Col><Text strong>{formatMontant(apercu.primeNette)}</Text></Col>
                                </Row>
                                <Row justify="space-between">
                                    <Col><Text>Coût de pièces</Text></Col>
                                    <Col><Text strong>{formatMontant(apercu.accessoires)}</Text></Col>
                                </Row>
                                <Row justify="space-between">
                                    <Col><Text>Taxes</Text></Col>
                                    <Col><Text strong>{formatMontant(apercu.taxes)}</Text></Col>
                                </Row>
                                <Divider style={{ margin: "8px 0" }} />
                                <Row justify="space-between">
                                    <Col><Text strong>MONTANT À RISTOURNER</Text></Col>
                                    <Col>
                                        <Text strong style={{ color: "#52c41a", fontSize: 16 }}>
                                            {formatMontant(apercu.primeTotale)}
                                        </Text>
                                    </Col>
                                </Row>
                            </div>
                        </>
                    )}
                </Spin>
            </ConfigProvider>
        </SDIModal>
    );
};

AnnulationEngins.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object,
};

export default AnnulationEngins;
